import type { DateTime } from "luxon";

export enum AlertType {
	Overstaffed = "overstaffed",
	CallIns = "call_ins",
	WeeklyOvertime = "weekly_overtime",
	Mismatch = "schedule_vs_actual_mismatch",
}

export enum AlertSeverity {
	Critical = "critical",
	Warning = "warning",
	Major = "major",
}

export type AlertSeverityRule = {
	id?: string;
	departmentId?: string;
	alertType: AlertType;
	metric: "count" | "percent" | "hours";
	operator: ">=" | ">" | "<" | "<=" | "=";
	threshold: number;
	severity: AlertSeverity;
	priority: number;
};

export type NurseInsightBadge = {
	insightType: string;
	i18nKey: string;
	i18nParams: Record<string, string | number>;
};

export type AlertAffectedNurse = {
	nurseId: number;
	employmentType: string;
	weeklyOtHours: number;
	hourlyWage: number | null;
	reason: string;
	insights?: NurseInsightBadge[];
};

export type AlertMismatchNurse = AlertAffectedNurse & {
	scheduledHours: number;
	actualHours: number;
	scheduledShiftType: string | null;
	actualShiftType: string | null;
};

// ── Typed alert detail variants ──────────────────────────────────

export type HppdStaffingDetail = {
	hppdPercent: number;
	maxValidHppdPercent: number;
	census: number;
	totalHours: number;
	excessCount: number;
	affectedNurses: AlertAffectedNurse[];
};

export type RatioStaffingDetail = {
	ratioLadderAmount: number;
	actualStaffCount: number;
	census: number;
	excessCount: number;
	affectedNurses: AlertAffectedNurse[];
};

export type CallInDetail = {
	totalCallIns: number;
	reasons: Record<string, number>;
	affectedNurses: AlertAffectedNurse[];
};

export type WeeklyOvertimeDetail = {
	totalOtHours: number;
	nurseCount: number;
	affectedNurses: AlertAffectedNurse[];
};

export type MismatchDetail = {
	totalMismatchedNurses: number;
	affectedNurses: AlertMismatchNurse[];
};

export type AlertDetail = HppdStaffingDetail | RatioStaffingDetail | CallInDetail | WeeklyOvertimeDetail | MismatchDetail;

// ── Main alert type ─────────────────────────────────────────────

export type DepartmentAlert = {
	alertType: AlertType;
	severity: AlertSeverity;
	count: number;
	value: number;
	detail: AlertDetail;
	source?: "actual" | "scheduled" | null;
};

export type DepartmentAlertRoleGroup = {
	roleGroup: string;
	alerts: DepartmentAlert[];
};

export type DepartmentAlertShiftType = {
	startTime: string;
	endTime: string;
	label: string;
	roleGroups: DepartmentAlertRoleGroup[];
};

export type DepartmentAlertDay = {
	date: DateTime;
	shiftTypes: DepartmentAlertShiftType[];
	totalAlerts: number;
	alertTypeCounts: Record<string, number>;
	severityCounts: Record<string, number>;
};

export type DepartmentAlertsResponse = {
	days: DepartmentAlertDay[];
	statsMode: string;
	defaultRules: AlertSeverityRule[];
};

export type AlertSortMode = "newest" | "oldest" | "most_alerts" | "fewest_alerts";

// Merged "Problems" card combining any of Overstaffed, Mismatch, WeeklyOvertime for the same role group.
// At least one must be present. All fields are optional since any subset can appear.
export type ProblemsAlert = {
	readonly alertKind: "problems";
	severity: AlertSeverity;
	overstaffed?: DepartmentAlert;
	mismatch?: DepartmentAlert;
	weeklyOvertime?: DepartmentAlert;
};

export type RoleGroupAlertSlot = DepartmentAlert | ProblemsAlert;

// View-layer variants that support merged alerts (used after showProblems post-processing)
export type DepartmentAlertRoleGroupView = {
	roleGroup: string;
	alerts: RoleGroupAlertSlot[];
};

export type DepartmentAlertShiftTypeView = {
	startTime: string;
	endTime: string;
	label: string;
	roleGroups: DepartmentAlertRoleGroupView[];
};

export type DepartmentAlertDayView = {
	date: DateTime;
	shiftTypes: DepartmentAlertShiftTypeView[];
	totalAlerts: number;
	alertTypeCounts: Record<string, number>;
	severityCounts: Record<string, number>;
};
